/**
 * PackingCompletionOverlay - Full-screen overlay after manual packing completion
 *
 * Shown when StarShipIt is NOT enabled and user clicks "Mark Complete".
 * At this point, the order is PACKED (not yet SHIPPED). The user must
 * complete the shipment externally in StarShipIt, then click "Done"
 * to mark the order as SHIPPED.
 *
 * Design:
 * - Indigo gradient background (distinct from blue bin placement and green shelf/complete)
 * - Animated checkmark at top
 * - Large heading for warehouse arm's-length visibility
 * - "Done" button triggers markManualComplete to transition PACKED -> SHIPPED
 *
 * Recovery: If browser crashes after PACKED but before "Done", order stays
 * PACKED (safe, recoverable state). Coordinator can follow up.
 *
 * Follows PlacementOverlay.tsx AlertDialog pattern.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogPortal,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CheckCircle2, Loader2 } from 'lucide-react';

interface PackingCompletionOverlayProps {
  open: boolean;
  onDone: () => void;
  onUndo?: () => void;
  isSubmitting?: boolean;
  error?: string | null;
  /** Order name to display in the heading (e.g. "#TH1234"). */
  orderName?: string;
  /** When true, instructions mention completing in StarShipIt externally. */
  starshipitEnabled?: boolean;
  /** When true, shows pickup-specific messaging instead of shipping. */
  isPickup?: boolean;
}

export function PackingCompletionOverlay({
  open,
  onDone,
  onUndo,
  isSubmitting = false,
  error = null,
  orderName,
  starshipitEnabled = false,
  isPickup = false,
}: PackingCompletionOverlayProps) {
  const { t } = useTranslation('production');
  const doneButtonRef = useRef<HTMLButtonElement>(null);

  if (!open) return null;

  return (
    <AlertDialog open={open}>
      <AlertDialogPortal>
        {/* Full-screen indigo gradient background */}
        <AlertDialogOverlay
          className={cn(
            'fixed inset-0 z-50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:duration-200',
            'bg-gradient-to-b from-indigo-950 via-indigo-900 to-indigo-950',
          )}
        />
        <AlertDialogContent
          className={cn(
            'fixed inset-0 z-50 flex items-center justify-center',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:duration-200',
            'border-0 bg-transparent p-4 shadow-none max-w-none w-auto',
            'translate-x-0 translate-y-0 left-0 top-0',
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            doneButtonRef.current?.focus();
          }}
        >
          <div className="w-full max-w-4xl mx-auto flex flex-col items-center gap-8">
            {/* Animated checkmark */}
            <div className="relative inline-block">
              <div className="absolute inset-0 rounded-full bg-indigo-400/20 animate-ping" />
              <CheckCircle2
                className="relative h-16 w-16 text-indigo-300"
                aria-hidden="true"
              />
            </div>

            {/* Heading and subtitle */}
            <div className="text-center space-y-4">
              <h2 className="text-6xl sm:text-7xl font-bold text-white">
                {isPickup
                  ? (orderName ? t('packer.completion.readyPickup', { orderName }) : t('packer.completion.readyPickupGeneric'))
                  : (orderName ? t('packer.completion.packedSuccess', { orderName }) : t('packer.completion.packedSuccessGeneric'))}
              </h2>
              <p
                className="text-xl text-indigo-200/80"
                id="packing-completion-instructions"
              >
                {isPickup
                  ? t('packer.completion.clickDonePickup')
                  : starshipitEnabled
                    ? t('packer.completion.completeInStarshipit')
                    : t('packer.completion.clickDoneShipped')}
              </p>
            </div>

            {/* Error display */}
            {error && (
              <div
                className="w-full flex items-center gap-4 px-6 py-5 rounded-xl bg-red-950/90 border-2 border-red-500/70"
                role="alert"
              >
                <p className="text-xl font-bold text-red-200">{error}</p>
              </div>
            )}

            {/* Done button */}
            <Button
              ref={doneButtonRef}
              onClick={onDone}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              aria-label={isPickup ? t('packer.completion.ariaConfirmPickup') : t('packer.completion.ariaMarkShipped')}
              aria-describedby="packing-completion-instructions"
              className={cn(
                'w-full h-20 text-2xl font-semibold gap-3',
                'bg-indigo-600 hover:bg-indigo-500 text-white',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'focus-visible:ring-0 focus-visible:ring-offset-0',
              )}
              size="lg"
            >
              {isSubmitting ? (
                <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
              ) : (
                <CheckCircle2 className="h-8 w-8" aria-hidden="true" />
              )}
              {isSubmitting ? t('packer.completion.completing') : t('common:done')}
              {!isSubmitting && (
                <kbd className="ml-1 rounded bg-indigo-500/40 px-2 py-0.5 text-sm font-normal text-indigo-200">
                  Enter
                </kbd>
              )}
            </Button>

            {/* Undo link — returns to packing view without marking shipped */}
            {onUndo && !isSubmitting && (
              <button
                onClick={onUndo}
                className="text-sm text-indigo-300/70 hover:text-white transition-colors"
                aria-label={t('packer.completion.ariaUndo')}
              >
                {t('placement.undo')}
                <kbd className="ml-1 rounded bg-indigo-400/20 px-1.5 py-0.5 text-xs text-indigo-300/50">
                  Backspace
                </kbd>
              </button>
            )}
          </div>

          {/* ARIA live region */}
          <div role="status" aria-live="polite" className="sr-only">
            {error
              ? `Error: ${error}. You can retry by clicking Done.`
              : isPickup
                ? t('packer.completion.clickDonePickup')
                : starshipitEnabled
                  ? t('packer.completion.completeInStarshipit')
                  : t('packer.completion.clickDoneShipped')}
          </div>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
}
